import { confirmar } from '@/components/ui/confirm-dialog';
import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { useMaterialStore } from '../store/material-store';
import { Material } from '../types/material.types';
import { MaterialTable } from '../components/material-table';
import { MaterialForm } from '../components/material-form';
import { TariffHistoryDialog } from '../components/tariff-history-dialog';
import { PriceListManager } from '../components/price-list-manager';
import { ServiceActivities } from '../components/service-activities';
import { ImageUpload } from '../components/image-upload';
import { MaterialPrices } from '../components/material-prices';

export function MaterialsPage() {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [tariffMaterial, setTariffMaterial] = useState<Material | null>(null);
  const [isTariffDialogOpen, setIsTariffDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { materials, isLoading, fetchMaterials, createMaterial, updateMaterial, deleteMaterial } =
    useMaterialStore();

  const filteredMaterials = materials.filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.code.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      (m.externalCode && m.externalCode.toLowerCase().includes(q)) ||
      (m.sku && m.sku.toLowerCase().includes(q)) ||
      (m.brand && m.brand.toLowerCase().includes(q)) ||
      (m.lob?.name && m.lob.name.toLowerCase().includes(q)) ||
      (m.type === 'PRODUCT' && 'producto'.includes(q)) ||
      (m.type === 'SERVICE' && 'servicio'.includes(q))
    );
  });

  useEffect(() => {
    fetchMaterials();
  }, []);

  const handleCreate = () => {
    setEditingMaterial(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (material: Material) => {
    setEditingMaterial(material);
    setIsDialogOpen(true);
  };

  const handleDelete = async (material: Material) => {
    if (
      !(await confirmar({
        title: '¿Estas seguro de que deseas eliminar este material?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;

    try {
      await deleteMaterial(material.id);
      toast({ title: 'Material eliminado', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleViewTariffHistory = (material: Material) => {
    setTariffMaterial(material);
    setIsTariffDialogOpen(true);
  };

  const handleSubmit = async (data: any) => {
    try {
      if (editingMaterial) {
        await updateMaterial(editingMaterial.id, data);
        toast({ title: 'Material actualizado', variant: 'success' });
      } else {
        await createMaterial(data);
        toast({ title: 'Material creado', variant: 'success' });
      }
      setIsDialogOpen(false);
      setEditingMaterial(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto p-8">
        <Tabs defaultValue="materials" className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="mb-3">
                <EtiquetaDePagina tono="neutro">Datos Maestros</EtiquetaDePagina>
              </div>
              <h1 className={CLASE_TITULO_DE_PAGINA}>Materiales</h1>
              <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
                Gestión de productos, servicios, listas de precios y partidas arancelarias
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TabsList>
                <TabsTrigger value="materials">Materiales</TabsTrigger>
                <TabsTrigger value="price-lists">Listas de Precios</TabsTrigger>
              </TabsList>
              <Button data-tour="materials-create-btn" onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Material
              </Button>
            </div>
          </div>

          <TabsContent value="materials">
            <Card data-tour="materials-table">
              <CardHeader>
                <CardTitle>Catálogo de Materiales</CardTitle>
                <CardDescription>
                  Materiales con partida arancelaria y porcentaje de gravamen histórico
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por código, nombre, SKU, marca, tipo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <MaterialTable
                  materials={filteredMaterials}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewTariffHistory={handleViewTariffHistory}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="price-lists">
            <Card>
              <CardContent className="pt-6">
                <PriceListManager />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? 'Editar Material' : 'Nuevo Material'}</DialogTitle>
            <DialogDescription>
              {editingMaterial
                ? 'Actualiza los datos del material'
                : 'Completa los datos para crear un nuevo material'}
            </DialogDescription>
          </DialogHeader>
          <MaterialForm
            material={editingMaterial || undefined}
            onSubmit={handleSubmit}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={isLoading}
          />
          {editingMaterial && (
            <div className="border-t pt-4 mt-4">
              <MaterialPrices
                materialId={editingMaterial.id}
                priceListEntries={editingMaterial.priceListEntries || []}
                onPricesChange={() => fetchMaterials()}
              />
            </div>
          )}
          {editingMaterial?.type === 'PRODUCT' && (
            <div className="border-t pt-4 mt-4">
              <ImageUpload
                materialId={editingMaterial.id}
                currentImageUrl={editingMaterial.imageUrl}
                onImageChange={() => fetchMaterials()}
              />
            </div>
          )}
          {editingMaterial?.type === 'SERVICE' && (
            <div className="border-t pt-4 mt-4">
              <ServiceActivities
                materialId={editingMaterial.id}
                activities={editingMaterial.serviceActivities || []}
                onActivitiesChange={() => fetchMaterials()}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tariff History Dialog */}
      <TariffHistoryDialog
        material={tariffMaterial}
        open={isTariffDialogOpen}
        onOpenChange={setIsTariffDialogOpen}
      />
    </AppLayout>
  );
}

export default MaterialsPage;
